import { BusinessDocType, FieldDictionaryUsedBy, RecursivePartial } from '../../../../types';
import { api } from '../../../api';
import { ApiResponse, PageResult } from '../../Utilities';
import {
    DocumentLayoutViewModel,
    DocumentSearchConfigurationViewModel,
    DocumentSearchViewModel,
    DocumentValidationResultViewModel,
    DocumentViewModel,
    FileViewModel,
    DocumentHistoryViewModel,
} from '../../ViewModels';

import {
    GetDocumentLayoutParams,
    GetDocumentSearchConfigurationByIdParams,
    GetDocumentSearchConfigurationParams,
    GetDocumentsForCompanyParams,
    GetDocumentsParams,
} from './Params';

// TODO: Update with proper validation failure types later
export interface DocumentResponse {
    ValidationFailures: any[] | null;
    ID: number;
}

export class DocumentApi {
    private route = '/Document';

    public getDocument(id: number): ApiResponse<DocumentViewModel> {
        return api().get(`${this.route}/${id}`);
    }

    public getDocumentsForCompany = (
        params: GetDocumentsForCompanyParams
    ): ApiResponse<PageResult<DocumentViewModel>> => {
        return api().get(this.route, { params });
    };

    public getDocuments(
        params: GetDocumentsParams,
        dynamicParams?: Record<string, string | boolean | number | null>
    ): ApiResponse<PageResult<DocumentSearchViewModel>> {
        return api().get(this.route, {
            params: {
                ...params,
                ...(dynamicParams ?? {}),
            },
        });
    }

    public getDocumentByBusinessDocId(params: {
        businessDocId: string;
        businessDocType: BusinessDocType;
    }): ApiResponse<PageResult<DocumentViewModel>> {
        return api().get(this.route, { params });
    }

    public getDocumentsCSV(params: GetDocumentsParams, dynamicParams?: Record<string, string | boolean | number>) {
        const headers = {
            Accept: 'text/csv',
        };

        return api().get(`${this.route}`, {
            params: {
                ...params,
                ...(dynamicParams ?? {}),
            },
            headers,
        });
    }

    public getDocumentRevisions = (id: number): ApiResponse<DocumentViewModel[]> => {
        return api().get(`${this.route}/revisions/${id}`);
    };

    public getDocumentSearchConfiguration(
        params: GetDocumentSearchConfigurationParams
    ): ApiResponse<DocumentSearchConfigurationViewModel[]> {
        return api().get(`${this.route}/SearchConfiguration`, { params });
    }

    /**
     * Fetch document history records by document id
     */
    public getDocumentHistory(documentId: number): ApiResponse<DocumentHistoryViewModel[]> {
        return api().get(`${this.route}/history`, { params: { documentid: documentId } });
    }

    /**
     * This endpoint gets the document search configuration by id.
     * It optionally returns all screen sizes related to the specific search configuration.
     */
    public getDocumentSearchConfigurationById(
        id: number,
        params: GetDocumentSearchConfigurationByIdParams
    ): ApiResponse<DocumentSearchConfigurationViewModel[]> {
        return api().get(`${this.route}/SearchConfiguration/${id}`, { params });
    }

    public getDocumentLayout(params: GetDocumentLayoutParams): ApiResponse<DocumentLayoutViewModel> {
        return api().get(`${this.route}/Layout`, { params });
    }

    public postDocumentLayout(
        params: { businessDocType: BusinessDocType; documentClass: number; buyerCompanyId: number },
        body: RecursivePartial<DocumentLayoutViewModel>
    ): ApiResponse<void> {
        return api().post(`${this.route}/Layout`, body, { params });
    }

    public deleteDocumentLayout(params: {
        businessDocType: BusinessDocType;
        businessDocSubType: number;
        buyerCompanyId: number;
    }): ApiResponse<void> {
        return api().delete(`${this.route}/Layout`, { params });
    }

    postDocumentValidation = (
        params: { buyerCompanyId: number; includeSourceDocumentErrors?: boolean },
        body: DocumentViewModel
    ): ApiResponse<DocumentValidationResultViewModel[]> => {
        return api().post(`${this.route}/Validate`, body, { params });
    };

    postDocumentValidationResult = (
        params: { documentId: number },
        body: RecursivePartial<DocumentValidationResultViewModel[]>
    ): ApiResponse<void> => {
        return api().post(`${this.route}/ValidationFailureDetail`, body, { params });
    };

    getGeneratePDF({
        params,
    }: {
        params: {
            documentId: number;
            businessDocType: BusinessDocType;
        };
    }): ApiResponse<FileViewModel> {
        return api().get(`${this.route}/GeneratePDF`, {
            params,
        });
    }

    getDraftGeneratePDF({
        params,
    }: {
        params: {
            businessDocumentDraftId: number;
            businessDocType: BusinessDocType;
        };
    }): ApiResponse<FileViewModel> {
        return api().get(`${this.route}/Draft/GeneratePDF`, {
            params,
        });
    }

    public getSkeleton(params: {
        businessDocType: BusinessDocType;
        client: FieldDictionaryUsedBy;
        buyerCompanyId?: number;
        customerId?: number;
        supplierCompanyId?: number;
        /**
         * Required for SIM documents (BusinessDocType.SIMDocument) so the backend
         * can return the correct per-class skeleton (W-9 = 1, COI = 2, Remittance = 4).
         * Omitting this for SIM causes GET /Document/Skeleton to return 400.
         */
        documentClass?: number;
    }): ApiResponse<RecursivePartial<DocumentViewModel>> {
        return api().get(`${this.route}/Skeleton`, { params });
    }

    public getDocumentDraft(id: number): ApiResponse<DocumentViewModel> {
        return api().get(`${this.route}/Draft/${id}`);
    }

    public postDocument(body: DocumentViewModel): ApiResponse<DocumentViewModel> {
        return api().post(`${this.route}`, body);
    }

    /**
     * Creates a Document and its attachments in a single call. The server
     * deletes the document if attaching fails, so the operation is atomic.
     */
    public postDocumentWithAttachments(body: DocumentViewModel, files: File[]): ApiResponse<DocumentResponse> {
        const formData = new FormData();
        formData.append('Document', JSON.stringify(body));
        for (const file of files) {
            formData.append('Files', file, file.name);
        }

        return api().post(`${this.route}/DocumentAttachment`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public postDocumentDraft(body: DocumentViewModel): ApiResponse<DocumentResponse> {
        return api().post(`${this.route}/Draft`, body);
    }

    /**
     * Upload supporting documents for a draft SIM (and other non-invoice) document.
     * Mirrors supplier-portal `saveDocumentDraftAttachments` (`value` form field).
     */
    public postDocumentDraftAttachments(params: {
        documentId: number;
        businessDocType: BusinessDocType;
        files: File[];
    }): ApiResponse<unknown[]> {
        const formData = new FormData();
        for (const file of params.files) {
            formData.append('value', file);
        }

        return api().post(`${this.route}/Draft/Attachments`, formData, {
            params: {
                businessDocType: params.businessDocType,
                documentId: params.documentId,
            },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public putDocumentDraft(id: number, body: DocumentViewModel): ApiResponse<DocumentViewModel> {
        return api().put(`${this.route}/Draft/${id}`, body);
    }

    public putDocument(id: number, body: DocumentViewModel): ApiResponse<DocumentResponse> {
        return api().put(`${this.route}/${id}`, body);
    }
}
